import * as XLSX from "xlsx";
import { StatementReconciliationItem } from "../../../../Types/Invoice";

export interface SupplierStatementExportData {
  invoiceId: number;
  supplier: string | null;
  invoiceDate: string | null;
  statementPeriod?: string | null;
  outstandingBalance: number | null;
  totalAmount?: number | null;
  description: string | null;
  reconciliationItems?: StatementReconciliationItem[] | null;
}

const MAX_EXPORTED_LINE_ITEMS = 2000;
const AMOUNT_FORMAT = "#,##0.00;[Red]-#,##0.00";
const DATE_FORMAT = "dd/mm/yyyy";
const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 255;
const DEFAULT_ROW_HEIGHT = 15;
const LINE_ITEM_HEADERS = [
  "Status",
  "Date",
  "Due Date",
  "Source (Supplier Name)",
  "Transaction Number / Reference",
  "Type",
  "Total Amount",
  "VAT Amount",
  "Net Amount",
  "Due Amount",
];

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "";

  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const ukDate = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
  if (ukDate) return value;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "";
  return [
    String(parsedDate.getUTCDate()).padStart(2, "0"),
    String(parsedDate.getUTCMonth() + 1).padStart(2, "0"),
    parsedDate.getUTCFullYear(),
  ].join("/");
};

const excelDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;

  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoDate) {
    return new Date(
      Date.UTC(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3])),
    );
  }

  const ukDate = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (ukDate) {
    return new Date(
      Date.UTC(Number(ukDate[3]), Number(ukDate[2]) - 1, Number(ukDate[1])),
    );
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const safeFilenamePart = (value: string): string =>
  value
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^[._ -]+|[._ -]+$/g, "");

export const getSupplierStatementExportFilename = (
  statement: SupplierStatementExportData,
): string => {
  const supplier =
    safeFilenamePart(statement.supplier?.trim() ?? "") || "UnknownSupplier";
  const date = formatDate(
    statement.statementPeriod ?? statement.invoiceDate,
  ).replace(/\//g, "-") || "UnknownDate";
  return `SupplierStatement_${supplier}_${date}_${statement.invoiceId}.xlsx`;
};

const numericAmount = (value: number | null | undefined): number | null =>
  value != null && Number.isFinite(Number(value)) ? Number(value) : null;

const getCellText = (value: string | number | Date | null): string => {
  if (value == null) return "";
  if (value instanceof Date) return formatDate(value.toISOString());
  return String(value);
};

const getColumnWidths = (
  rows: Array<Array<string | number | Date | null>>,
): XLSX.ColInfo[] => {
  const columnCount = Math.max(...rows.map((row) => row.length));
  return Array.from({ length: columnCount }, (_, columnIndex) => {
    const maxLength = rows.reduce((max, row) => {
      const longestLine = getCellText(row[columnIndex])
        .split(/\r\n|\r|\n/)
        .reduce((lineMax, line) => Math.max(lineMax, line.length), 0);
      return Math.max(max, longestLine);
    }, 0);

    return {
      wch: Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(MIN_COLUMN_WIDTH, maxLength + 2),
      ),
    };
  });
};

const getRowHeights = (
  rows: Array<Array<string | number | Date | null>>,
  columns: XLSX.ColInfo[],
): XLSX.RowInfo[] =>
  rows.map((row) => {
    const wrappedLineCount = row.reduce<number>((maxLines, value, columnIndex) => {
      const width = Math.max(
        MIN_COLUMN_WIDTH,
        (columns[columnIndex]?.wch ?? MIN_COLUMN_WIDTH) - 2,
      );
      const lines = getCellText(value).split(/\r\n|\r|\n/);
      const wrappedLines = lines.reduce(
        (count, line) => count + Math.max(1, Math.ceil(line.length / width)),
        0,
      );
      return Math.max(maxLines, wrappedLines);
    }, 1);

    return { hpt: Math.max(DEFAULT_ROW_HEIGHT, wrappedLineCount * DEFAULT_ROW_HEIGHT) };
  });

const wrapLongText = (value: string, width: number): string => {
  const maxLineLength = Math.max(MIN_COLUMN_WIDTH, width - 2);

  return value
    .split(/\r\n|\r|\n/)
    .flatMap((line) => {
      const wrappedLines: string[] = [];
      let remaining = line;

      while (remaining.length > maxLineLength) {
        let breakAt = remaining.lastIndexOf(" ", maxLineLength);
        if (breakAt <= 0) breakAt = maxLineLength;
        else breakAt += 1;
        wrappedLines.push(remaining.slice(0, breakAt));
        remaining = remaining.slice(breakAt);
      }

      wrappedLines.push(remaining);
      return wrappedLines;
    })
    .join("\n");
};

export const createSupplierStatementWorkbook = (
  statement: SupplierStatementExportData,
): XLSX.WorkBook => {
  const lineItems = (statement.reconciliationItems ?? []).slice(
    0,
    MAX_EXPORTED_LINE_ITEMS,
  );
  const rows: Array<Array<string | number | Date | null>> = [
    ["Statement Details"],
    ["Field", "Value"],
    ["Item ID", statement.invoiceId],
    ["Supplier", statement.supplier ?? ""],
    ["Date", excelDate(statement.statementPeriod ?? statement.invoiceDate)],
    [
      "Outstanding Balance",
      numericAmount(statement.outstandingBalance ?? statement.totalAmount),
    ],
    ["Note", statement.description ?? ""],
    [],
    ["Extracted Line Items"],
    LINE_ITEM_HEADERS,
  ];

  if (lineItems.length === 0) {
    rows.push(["No line items were extracted for this statement."]);
  } else {
    lineItems.forEach((item) => {
      rows.push([
        item.status ?? "",
        excelDate(item.transactionDate),
        excelDate(item.dueDate),
        item.source ?? "",
        item.transactionReference ?? "",
        item.type ?? "",
        numericAmount(item.totalAmount),
        numericAmount(item.vatAmount),
        numericAmount(item.netAmount),
        numericAmount(item.dueAmount),
      ]);
    });
  }

  const columnWidths = getColumnWidths(rows);
  const wrappedRows = rows.map((row, rowIndex) =>
    row.map((value, columnIndex) =>
      typeof value === "string" &&
      rowIndex !== 0 &&
      rowIndex !== 8 &&
      !(rowIndex === 10 && lineItems.length === 0)
        ? wrapLongText(value, columnWidths[columnIndex].wch ?? MIN_COLUMN_WIDTH)
        : value,
    ),
  );
  const worksheet = XLSX.utils.aoa_to_sheet(wrappedRows);
  const merges: XLSX.Range[] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 8, c: 0 }, e: { r: 8, c: 9 } },
  ];
  if (lineItems.length === 0) {
    merges.push({ s: { r: 10, c: 0 }, e: { r: 10, c: 9 } });
  } else {
    worksheet["!autofilter"] = { ref: `A10:J${rows.length}` };
  }
  worksheet["!merges"] = merges;
  worksheet["!cols"] = columnWidths;
  worksheet["!rows"] = getRowHeights(wrappedRows, columnWidths);

  const statementDateCell = worksheet.B5;
  if (statementDateCell && statementDateCell.v instanceof Date) {
    statementDateCell.z = DATE_FORMAT;
  }
  for (let row = 11; row <= wrappedRows.length; row += 1) {
    for (const column of ["B", "C"]) {
      const cell = worksheet[`${column}${row}`];
      if (cell && cell.v instanceof Date) cell.z = DATE_FORMAT;
    }
    for (const column of ["G", "H", "I", "J"]) {
      const cell = worksheet[`${column}${row}`];
      if (cell && typeof cell.v === "number") cell.z = AMOUNT_FORMAT;
    }
  }
  const outstandingBalanceCell = worksheet.B6;
  if (
    outstandingBalanceCell &&
    typeof outstandingBalanceCell.v === "number"
  ) {
    outstandingBalanceCell.z = AMOUNT_FORMAT;
  }

  wrappedRows.forEach((row, rowIndex) => {
    row.forEach((_, columnIndex) => {
      const address = XLSX.utils.encode_cell({
        r: rowIndex,
        c: columnIndex,
      });
      const cell = worksheet[address];
      if (cell) {
        cell.s = {
          ...cell.s,
          alignment: {
            ...cell.s?.alignment,
            vertical: "top",
            wrapText: true,
          },
        };
      }
    });
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Supplier Statement");
  return workbook;
};
